import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectUser } from "../../../store/slices/auth/slice";
import {
  selectPasswordChangeLoading,
  selectPasswordChangeError,
  selectPasswordChangeMessage,
  clearPasswordChangeState,
} from "../../../store/slices/user/slice";
import { changePassword } from "../../../store/slices/user/asyncThunks";
import BandLayout from "../BandLayout/BandLayout";
import VerificationStatusBadge, {
  getVerificationStatus,
} from "../../../components/utils/VerificationStatusBadge";
import {
  FiUser,
  FiMail,
  FiLock,
  FiCheck,
  FiEye,
  FiEyeOff,
  FiSave,
  FiAlertCircle,
  FiInfo,
  FiSettings,
} from "react-icons/fi";
import {
  PageSection,
  HeaderSection,
  HeaderTitle,
  HeaderSubtitle,
  ContentGrid,
  InfoCard,
  InfoSection,
  InfoHeader,
  InfoTitle,
  InfoGrid,
  InfoItem,
  InfoLabel,
  InfoValue,
  FormCard,
  FormSection,
  FormHeader,
  FormTitle,
  FormGrid,
  FormGroup,
  Label,
  Input,
  InputWrapper,
  PasswordToggle,
  Button,
  Alert,
  VerificationCard,
  VerificationHeader,
  VerificationStatus,
  VerificationText,
  LoadingSpinner,
} from "./BandSettings.styles";
import {
  selectEmailVerificationData,
  selectEmailVerificationStatus,
} from "../../../store/slices/emailVerification/slice";
import EmailVerificationModal from "../../../components/utils/EmailVerificationModal";
import useScrollLock from "../../../hooks/useScrollLock";
import { getVerificationStatus as getVerificationStatusAsync } from "../../../store/slices/emailVerification/asyncThunks";

const BandSettings = () => {
  const dispatch = useDispatch();
  const user = useSelector(selectUser);

  // Password change Redux state
  const passwordChangeLoading = useSelector(selectPasswordChangeLoading);
  const passwordChangeError = useSelector(selectPasswordChangeError);
  const passwordChangeMessage = useSelector(selectPasswordChangeMessage);

  const verificationData = useSelector(selectEmailVerificationData);
  const verificationStatus = useSelector(selectEmailVerificationStatus);
  const [emailVerificationModal, setEmailVerificationModal] = useState(false);

  // Password change state
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    dispatch(clearPasswordChangeState());

    // Validation
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      return;
    }

    // Dispatch password change action
    const result = await dispatch(
      changePassword({
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
      })
    );

    // Clear form on success
    if (changePassword.fulfilled.match(result)) {
      setPasswordForm({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    }
  };
  // Email verification functions
  const checkVerificationStatus = () => {
    if (!user || user.role !== "band") return;
    dispatch(getVerificationStatusAsync());
  };

  const handleOpenVerificationModal = () => {
    setEmailVerificationModal(true);
  };

  const handleCloseVerificationModal = () => {
    setEmailVerificationModal(false);
    // Recheck verification status after modal closes
    setTimeout(() => {
      checkVerificationStatus();
    }, 1000);
  };
  useScrollLock(emailVerificationModal);

  return (
    <BandLayout>
      <PageSection $active>
        <HeaderSection>
          <HeaderTitle>Podešavanja Naloga</HeaderTitle>
          <HeaderSubtitle>
            Upravljajte svojim nalogom i sigurnosnim podešavanjima
          </HeaderSubtitle>
        </HeaderSection>

        <ContentGrid>
          {/* Account Information */}
          <InfoCard>
            <InfoSection>
              <InfoHeader>
                <InfoTitle>
                  <FiInfo />
                  Informacije o Nalogu
                </InfoTitle>
              </InfoHeader>
              <InfoGrid>
                <InfoItem>
                  <InfoLabel>
                    <FiUser />
                    Korisničko ime
                  </InfoLabel>
                  <InfoValue>{user?.username}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>
                    <FiUser />
                    Ime benda
                  </InfoLabel>
                  <InfoValue>{user?.bandName || "Nije uneseno"}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>
                    <FiMail />
                    Email adresa
                  </InfoLabel>
                  <InfoValue>{user?.email}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>
                    <FiSettings />
                    Tip naloga
                  </InfoLabel>
                  <InfoValue>Band</InfoValue>
                </InfoItem>
              </InfoGrid>
            </InfoSection>
          </InfoCard>

          {/* Email Verification */}
          <VerificationCard>
            <VerificationHeader>
              <InfoTitle>
                <FiMail />
                Verifikacija Email-a
              </InfoTitle>
            </VerificationHeader>
            <VerificationStatus>
              <VerificationText>
                <strong>
                  {verificationData?.emailVerified
                    ? "Email je verifikovan"
                    : "Email nije verifikovan"}
                </strong>
                <span>
                  {verificationData?.emailVerified
                    ? "Vaš email je uspešno verifikovan"
                    : "Molimo verifikujte svoj email za dodatnu sigurnost"}
                </span>
              </VerificationText>
            </VerificationStatus>

            <VerificationStatusBadge
              loading={verificationStatus.loading}
              emailVerificationModal={emailVerificationModal}
              setEmailVerificationModal={setEmailVerificationModal}
              user={user}
              status={getVerificationStatus(verificationData)}
              onClick={
                verificationData?.emailVerified
                  ? null
                  : handleOpenVerificationModal
              }
              showTooltip
              size="lg"
              big
            />
          </VerificationCard>

          {/* Password Change */}
          <FormCard>
            <FormSection>
              <FormHeader>
                <FormTitle>
                  <FiLock />
                  Promena Šifre
                </FormTitle>
              </FormHeader>
              <form onSubmit={handlePasswordSubmit}>
                <FormGrid>
                  <FormGroup>
                    <Label>Trenutna šifra</Label>
                    <InputWrapper>
                      <Input
                        type={showPasswords.current ? "text" : "password"}
                        name="currentPassword"
                        value={passwordForm.currentPassword}
                        onChange={handlePasswordChange}
                        required
                      />
                      <PasswordToggle
                        type="button"
                        onClick={() => togglePasswordVisibility("current")}
                      >
                        {showPasswords.current ? <FiEyeOff /> : <FiEye />}
                      </PasswordToggle>
                    </InputWrapper>
                  </FormGroup>

                  <FormGroup>
                    <Label>Nova šifra</Label>
                    <InputWrapper>
                      <Input
                        type={showPasswords.new ? "text" : "password"}
                        name="newPassword"
                        value={passwordForm.newPassword}
                        onChange={handlePasswordChange}
                        required
                        minLength={6}
                      />
                      <PasswordToggle
                        type="button"
                        onClick={() => togglePasswordVisibility("new")}
                      >
                        {showPasswords.new ? <FiEyeOff /> : <FiEye />}
                      </PasswordToggle>
                    </InputWrapper>
                  </FormGroup>

                  <FormGroup>
                    <Label>Potvrdi novu šifru</Label>
                    <InputWrapper>
                      <Input
                        type={showPasswords.confirm ? "text" : "password"}
                        name="confirmPassword"
                        value={passwordForm.confirmPassword}
                        onChange={handlePasswordChange}
                        required
                        minLength={6}
                      />
                      <PasswordToggle
                        type="button"
                        onClick={() => togglePasswordVisibility("confirm")}
                      >
                        {showPasswords.confirm ? <FiEyeOff /> : <FiEye />}
                      </PasswordToggle>
                    </InputWrapper>
                  </FormGroup>

                  <Button type="submit" disabled={passwordChangeLoading}>
                    {passwordChangeLoading ? (
                      <>
                        <LoadingSpinner />
                        Čuva se...
                      </>
                    ) : (
                      <>
                        <FiSave />
                        Promeni Šifru
                      </>
                    )}
                  </Button>
                </FormGrid>
              </form>
            </FormSection>
          </FormCard>
        </ContentGrid>

        {/* Email Verification */}
        <EmailVerificationModal
          isOpen={emailVerificationModal}
          onClose={handleCloseVerificationModal}
          currentUser={user}
        />

        {/* Messages */}
        {passwordChangeMessage && (
          <Alert $type="success">
            <FiCheck />
            {passwordChangeMessage}
          </Alert>
        )}

        {passwordChangeError && (
          <Alert $type="error">
            <FiAlertCircle />
            {passwordChangeError}
          </Alert>
        )}

        {/* Form validation errors */}
        {passwordForm.newPassword !== passwordForm.confirmPassword &&
          passwordForm.confirmPassword && (
            <Alert $type="error">
              <FiAlertCircle />
              Nova šifra i potvrda se ne poklapaju
            </Alert>
          )}

        {passwordForm.newPassword.length > 0 &&
          passwordForm.newPassword.length < 6 && (
            <Alert $type="error">
              <FiAlertCircle />
              Nova šifra mora imati najmanje 6 karaktera
            </Alert>
          )}
      </PageSection>
    </BandLayout>
  );
};

export default BandSettings;
